import { t as tr } from './i18n';
import type { Supplier, SupplierInput } from './types';

/**
 * Importul agendei de furnizori dintr-un tabel: rânduri lipite din Excel sau
 * Google Sheets (despărțite cu tab) ori un fișier CSV (virgulă sau punct și
 * virgulă, ca în Excelul românesc). Coloanele se recunosc după capul de tabel;
 * fără cap de tabel, după conținut (emailul, telefonul, primul text = numele).
 *
 * Mai multe rânduri cu același nume de firmă devin un singur furnizor, cu câte
 * o persoană de contact pe rând: așa arată de obicei o agendă ținută în Excel.
 */

export type ImportField = 'name' | 'email' | 'contact' | 'phone' | 'categories' | 'cui' | 'address';

/** Cuvintele după care se recunoaște o coloană, fără diacritice și litere mici. */
const HEADERS: Record<ImportField, RegExp> = {
  name: /^(nume( firma| furnizor)?|firma|furnizor|denumire|societate|company|supplier|name)$/,
  email: /^(e-?mail|adresa( de)? e-?mail|mail)$/,
  contact: /^(persoana( de contact)?|contact|reprezentant|nume contact|contact person)$/,
  phone: /^(telefon|tel\.?|mobil|phone|nr\.? telefon)$/,
  categories: /^(categori[ei]|domeniu|domenii|produse|category|categories)$/,
  cui: /^(cui|cif|cod fiscal|cod tva|vat( id)?)$/,
  address: /^(adresa|sediu|address)$/,
};

const EMAIL = /[^\s@,;<>()"']+@[^\s@,;<>()"']+\.[a-z]{2,}/gi;
const PHONE = /^\+?[\d\s().-]{7,}$/;

const plain = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[:*]/g, '').replace(/\s+/g, ' ').trim();

/** Rândurile tabelului, cu ghilimelele CSV respectate. */
export function splitTable(text: string): string[][] {
  const src = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n');
  const first = src.split('\n').find((l) => l.trim()) ?? '';
  const sep = first.includes('\t')
    ? '\t'
    : (first.match(/;/g)?.length ?? 0) > (first.match(/,/g)?.length ?? 0)
      ? ';'
      : ',';
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === '') quoted = true;
    else if (ch === sep) {
      row.push(cell.trim());
      cell = '';
    } else if (ch === '\n') {
      row.push(cell.trim());
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  row.push(cell.trim());
  rows.push(row);
  return rows.filter((r) => r.some((c) => c !== ''));
}

/** Ce coloană e ce: din capul de tabel, dacă există, altfel din conținut. */
export function detectColumns(rows: string[][]): {
  header: boolean;
  columns: Partial<Record<ImportField, number>>;
} {
  const head = rows[0] ?? [];
  const columns: Partial<Record<ImportField, number>> = {};
  head.forEach((cell, i) => {
    const h = plain(cell);
    for (const f of Object.keys(HEADERS) as ImportField[]) {
      if (columns[f] === undefined && HEADERS[f].test(h)) {
        columns[f] = i;
        break;
      }
    }
  });
  if (columns.name !== undefined || columns.email !== undefined) return { header: true, columns };

  // Fără cap de tabel: după ce găsim în primele rânduri.
  const sample = rows.slice(0, 20);
  const width = Math.max(...sample.map((r) => r.length), 0);
  const share = (i: number, test: (v: string) => boolean) => {
    const cells = sample.map((r) => r[i] ?? '').filter(Boolean);
    return cells.length ? cells.filter(test).length / cells.length : 0;
  };
  const guessed: Partial<Record<ImportField, number>> = {};
  for (let i = 0; i < width; i++) {
    if (guessed.email === undefined && share(i, (v) => /@/.test(v)) >= 0.6) guessed.email = i;
    else if (guessed.phone === undefined && share(i, (v) => PHONE.test(v)) >= 0.6) guessed.phone = i;
    else if (guessed.name === undefined && share(i, (v) => /[a-zăâîșț]/i.test(v)) >= 0.6) guessed.name = i;
  }
  return { header: false, columns: guessed };
}

export type ImportStatus = 'new' | 'exists' | 'invalid';

export interface ImportRow {
  /** rândul din tabel (de la 1, cu tot cu capul), ca omul să-l găsească */
  line: number;
  name: string;
  emails: string[];
  contact: string;
  phone: string;
  categories: string[];
  cui: string;
  address: string;
}

export interface ImportSupplier {
  name: string;
  categories: string[];
  cui: string;
  address: string;
  contacts: { name: string; email: string; phone: string }[];
  /** rândurile din care s-a strâns */
  lines: number[];
  status: ImportStatus;
  /** de ce nu intră: „lipsește emailul”, „există deja în agendă” */
  reason: string;
}

const splitList = (v: string) =>
  v
    .split(/[,;/|]/)
    .map((x) => x.trim().replace(/\s+/g, ' '))
    .filter(Boolean);

export function readRows(text: string): {
  rows: ImportRow[];
  header: boolean;
  columns: Partial<Record<ImportField, number>>;
} {
  const table = splitTable(text);
  const { header, columns } = detectColumns(table);
  const get = (r: string[], f: ImportField) =>
    columns[f] === undefined ? '' : (r[columns[f]!] ?? '').trim();
  const rows = (header ? table.slice(1) : table).map((r, i) => ({
    line: i + (header ? 2 : 1),
    name: get(r, 'name').replace(/\s+/g, ' '),
    // un email scris „Nume <adresa>” sau două adrese într-o celulă
    emails: [...new Set((get(r, 'email').match(EMAIL) ?? []).map((e) => e.toLowerCase()))],
    contact: get(r, 'contact').replace(/\s+/g, ' '),
    phone: get(r, 'phone'),
    categories: splitList(get(r, 'categories')),
    cui: get(r, 'cui').replace(/\s+/g, '').toUpperCase(),
    address: get(r, 'address').replace(/\s+/g, ' '),
  }));
  return { rows, header, columns };
}

/**
 * Furnizorii de importat, cu starea fiecăruia față de agenda de acum. Un
 * furnizor există deja dacă are același nume (fără diacritice și majuscule)
 * sau dacă vreuna din adresele lui e deja la un furnizor din agendă.
 */
export function planImport(
  text: string,
  existing: Pick<Supplier, 'name' | 'contacts'>[],
  fallbackCategory: string,
  /**
   * Codul fiscal și adresa sunt obligatorii: apar pe comenzi. Unde e ANAF
   * (România), adresa lipsă o completează serverul după CUI.
   */
  need: { taxLabel: string; anafAddress: boolean } = { taxLabel: 'CUI', anafAddress: true },
): ImportSupplier[] {
  const { rows } = readRows(text);
  const knownNames = new Set(existing.map((s) => plain(s.name)));
  const knownEmails = new Set(existing.flatMap((s) => s.contacts.map((c) => c.email.toLowerCase())));

  const byName = new Map<string, ImportSupplier>();
  const out: ImportSupplier[] = [];
  for (const r of rows) {
    if (!r.name && !r.emails.length) continue;
    const key = plain(r.name);
    let s = key ? byName.get(key) : undefined;
    if (!s) {
      s = {
        name: r.name,
        categories: [],
        cui: r.cui,
        address: r.address,
        contacts: [],
        lines: [],
        status: 'new',
        reason: '',
      };
      if (key) byName.set(key, s);
      out.push(s);
    }
    s.lines.push(r.line);
    for (const c of r.categories) {
      if (!s.categories.some((x) => x.toLowerCase() === c.toLowerCase())) s.categories.push(c);
    }
    if (!s.cui) s.cui = r.cui;
    if (!s.address) s.address = r.address;
    r.emails.forEach((email, i) => {
      if (!s!.contacts.some((c) => c.email === email)) {
        s!.contacts.push({ name: i === 0 ? r.contact : '', email, phone: i === 0 ? r.phone : '' });
      }
    });
  }

  for (const s of out) {
    if (!s.categories.length && fallbackCategory.trim()) s.categories = [fallbackCategory.trim()];
    if (!s.name) [s.status, s.reason] = ['invalid', tr('lipsește numele firmei')];
    else if (!s.contacts.length) [s.status, s.reason] = ['invalid', tr('lipsește emailul')];
    else if (!s.categories.length) [s.status, s.reason] = ['invalid', tr('lipsește categoria')];
    else if (knownNames.has(plain(s.name)) || s.contacts.some((c) => knownEmails.has(c.email))) {
      [s.status, s.reason] = ['exists', tr('există deja în agendă')];
    }
    // CUI-ul și adresa trebuie abia pe comandă: acolo se cer, dacă lipsesc
    else if (!s.cui) s.reason = tr('fără {0}: se cere la prima comandă', need.taxLabel);
    else if (!s.address) {
      s.reason = need.anafAddress ? tr('adresa vine de la ANAF') : tr('fără adresă: se cere la prima comandă');
    }
  }
  return out;
}

/** Ce se trimite la server: doar furnizorii noi, cu prima persoană principală. */
export function importPayload(plan: ImportSupplier[]): SupplierInput[] {
  return plan
    .filter((s) => s.status === 'new')
    .map((s) => ({
      name: s.name,
      categories: s.categories,
      cui: s.cui,
      address: s.address,
      contacts: s.contacts.slice(0, 20).map((c, i) => ({ ...c, isPrimary: i === 0 })),
    }));
}

/** Cel mult atâția furnizori dintr-o dată. */
export const IMPORT_MAX = 500;
